import {
    React, useState, styled, useEffect, firstNonNil, CSSObject, useToggle, useEventListener, isSSR,
} from '@common'
import { colors, media } from '@theme'
import deepmerge from 'deepmerge'
import { LeaderLine, LeaderLineProps, HiddenOptions } from '@components/leader-line'
import { DeviceSize, Message, LINE_COLOR, MessageBoxProps, DeviceSizes } from './types'
import { useMemo } from 'react'


const MessageBoxWrapper = styled.div({
    boxShadow: '0 2px 8px rgba(50,50,71,.16)',
    padding: '5px 10px',

    zIndex: 2,
    backgroundColor: 'white',
    h3: {
        fontSize: 14,
        color: LINE_COLOR,
        fontWeight: 600,
        marginBottom: 4,
    },
    p: {
        fontSize: 12,
        color: colors.lightText,
        marginBottom: 0,
    }
})

const ExtMessagBox:FC<MessageBoxProps> = ({ message: m, ...props }) => {
    const [target, setTarget] = useState<HTMLDivElement | null>(null)
    const message = useMemo(() => ({
        ...m,
        hideOnScroll: false
    }), [m])
    useEffect(() => {
        if (message.externalSelector) {
            const target = document.querySelector<HTMLDivElement>(message.externalSelector)
            setTarget(target || null)
        }
    }, [message.externalSelector])
    return  <MessageBox {...props} message={message} target={target} />
}

const MARGIN = '1rem'

function marginsForMessage(m: Message, withDefaults = true) {
    const pos = m.position

    const css:CSSObject = {
        maxWidth: m.style?.maxWidth || withDefaults ? 300 : undefined
    }

    if (!pos) return css

    if (pos.startsWith('l')) {
        css.marginRight = MARGIN
        css.maxWidth = m.style?.maxWidth || withDefaults ? 200 : undefined
    }
    if (pos.startsWith('r')) {
        css.marginLeft = MARGIN
        css.maxWidth = m.style?.maxWidth || withDefaults ? 200 : undefined
    }
    if (pos.startsWith('t')) css.marginBottom = MARGIN
    if (pos.startsWith('b')) css.marginTop = MARGIN

    return css
}

function cssForMessage(m: Message, sizes: DeviceSize[], withDefaults = true):CSSObject {
    const pos = m.position

    const def:CSSObject = {
        ...marginsForMessage(m, withDefaults),
        gridColumn: pos,
        gridRow: pos,
        display: m.hidden === true ? 'none' : 'block',
        alignSelf: m.align ? m.align : withDefaults ? 'center' : undefined,
        justifySelf: m.justify ? m.justify : withDefaults ? 'center' : undefined,
        ...(m.style || {}),
    }

    const css: CSSObject = def
    for (const size of sizes) {
        const szDef = m[size]
        const sizeCSS =  szDef ? cssForMessage(szDef, sizes, false) : undefined

        if (sizeCSS) {
            css[media[size]] = sizeCSS
        }

        if (m[size]?.hidden == true) {
            css[`.${size} &`] = {
                display: 'none',
            }
        }
    }

    return css
}


const MessageBox:FC<MessageBoxProps> = ({
    message, hideLine: defaultHiddenLine, target, deviceSize, displaySize, className, imageReady, sizes,
}) => {

    const [boxRef, setRef] = useState<HTMLDivElement | null>(null)

    const deviceOpts = deepmerge(
        message,
        message[deviceSize] || {}, {
    })

    const lineOpts = useMemo<NonNullable<LeaderLineProps['options']>>(() => ({
        ...message,
        size: 2,
        deviceSize,
        displaySize,
        hidden: deepmerge( {
            bottom: { endSocket: 'top' },
            top: { endSocket: 'bottom' }
        } satisfies HiddenOptions , (deviceOpts.hidden || {}) as any ),
        box: message[deviceSize]?.[displaySize]?.box || message[displaySize]?.box || message.box,
        pointTo: message[deviceSize]?.[displaySize]?.pointTo || message[displaySize]?.pointTo || message.pointTo,
        pointFrom: deviceOpts[displaySize]?.pointFrom || deviceOpts.pointFrom || message.pointFrom,
        startSocket: deviceOpts[displaySize]?.startSocket || deviceOpts.startSocket || message.startSocket,
        endSocket: deviceOpts[displaySize]?.endSocket || deviceOpts.endSocket || message.endSocket,
    }), [deviceSize, displaySize, deviceOpts, message])

    const hideLine = firstNonNil(message[deviceSize]?.hideLine, message[deviceSize]?.[displaySize]?.hideLine, defaultHiddenLine)
    const isHidden = (message[deviceSize]?.hidden === true || message[displaySize]?.hidden === true || message.hidden === true)
    const hasLine = !isSSR && !hideLine && !isHidden && imageReady && (lineOpts.pointTo || lineOpts.box) && target && boxRef

    return (
        <MessageBoxWrapper ref={setRef}
            className={className}
            css={cssForMessage(message, sizes)}
        >
            <h3>{message.title}</h3>
            <div dangerouslySetInnerHTML={{ __html: message.message }} />
            {hasLine && <LeaderLine start={boxRef} end={target} options={lineOpts} />}
        </MessageBoxWrapper>
    )
}

type MessagesProps = {
    target:HTMLDivElement | null, messages: Message[],
    deviceSize: DeviceSize
    displaySize: DeviceSize
    sizes?:  DeviceSize[]
}

export const Messages:FC<MessagesProps> = ({ messages, target, displaySize, sizes=DeviceSizes, ...props}) => {
    const {isEnabled: imageReady, setEnabled} = useToggle(false)

    useEffect(() => {
        if (target?.querySelector<HTMLImageElement>(`img.${displaySize}`)?.complete) {
            setEnabled()
        }
    }, [target, displaySize, setEnabled])

    useEventListener<'load', HTMLElement>('load', () => {
        setEnabled()
    }, { target: target, useCapture: true, pause: !target })

    const rendered = messages.map((m, i) => {
        if (!m) return null
        const Comp = m.externalSelector ? ExtMessagBox : MessageBox
        return (
            <Comp
                key={`${m.position}-${i}`}
                displaySize={displaySize}
                target={target}
                message={m}
                sizes={sizes}
                imageReady={imageReady}
                {...props}
            />
        )
    })

    return <>{rendered}</>
}
